import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { QUESTIONS, QUESTIONS_MAP, TOTAL_QUESTIONS } from '@/lib/questions';
import { Assessment, ComputedResults } from '@/lib/assessment-types';

const ASSESSMENT_STORAGE_KEY = 'assessment_id';
const ASSESSMENT_SECRET_KEY = 'assessment_session_secret';

interface UseAssessmentReturn {
  assessment: Assessment | null;
  sessionSecret: string | null;
  responses: Map<string, number>;
  currentIndex: number;
  loading: boolean;
  saving: boolean;
  error: string | null;
  progressPercentage: number;
  questionOrder: string[];
  currentQuestionId: string | null;
  currentQuestionText: string | null;
  isComplete: boolean;
  hasExistingAssessment: boolean;
  totalQuestions: number;
  startAssessment: (info: {
    name: string;
    email: string;
    role?: string;
    practice_name?: string;
    token_id?: string;
  }) => Promise<{ id: string; sessionSecret: string } | null>;
  saveResponse: (questionId: string, value: number) => Promise<void>;
  goToPrevious: () => void;
  completeAssessment: () => Promise<string | null>;
  clearAssessment: () => void;
  estimatedTimeRemaining: () => number;
}

export function useAssessment(): UseAssessmentReturn {
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [sessionSecret, setSessionSecret] = useState<string | null>(null);
  const [responses, setResponses] = useState<Map<string, number>>(new Map());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for existing assessment on mount
  useEffect(() => {
    async function checkExistingAssessment() {
      const storedAssessmentId = localStorage.getItem(ASSESSMENT_STORAGE_KEY);
      const storedSecret = localStorage.getItem(ASSESSMENT_SECRET_KEY);

      if (!storedAssessmentId || !storedSecret) {
        localStorage.removeItem(ASSESSMENT_STORAGE_KEY);
        localStorage.removeItem(ASSESSMENT_SECRET_KEY);
        setLoading(false);
        return;
      }

      try {
        // Use secure RPC function to get assessment with secret validation
        const { data: assessmentData, error: assessmentError } = await supabase
          .rpc('get_assessment_by_secret', {
            p_assessment_id: storedAssessmentId,
            p_session_secret: storedSecret,
          });

        if (assessmentError || !assessmentData || assessmentData.length === 0) {
          localStorage.removeItem(ASSESSMENT_STORAGE_KEY);
          localStorage.removeItem(ASSESSMENT_SECRET_KEY);
          setLoading(false);
          return;
        }

        const assessmentInfo = assessmentData[0];

        // Check if assessment is already completed
        if (assessmentInfo.completed_at || assessmentInfo.status === 'complete') {
          localStorage.removeItem(ASSESSMENT_STORAGE_KEY);
          localStorage.removeItem(ASSESSMENT_SECRET_KEY);
          setLoading(false);
          return;
        }

        setAssessment(assessmentInfo as Assessment);
        setSessionSecret(storedSecret);
        setCurrentIndex(assessmentInfo.current_question_index);

        // Load existing responses
        const { data: responsesData } = await supabase
          .rpc('get_assessment_responses_by_secret', {
            p_assessment_id: storedAssessmentId,
            p_session_secret: storedSecret,
          });

        if (responsesData) {
          const responseMap = new Map(
            responsesData.map((r: { question_id: string; response_value: number }) => 
              [r.question_id, r.response_value]
            )
          );
          setResponses(responseMap);
        }
      } catch (err) {
        console.error('Error loading assessment:', err);
        localStorage.removeItem(ASSESSMENT_STORAGE_KEY);
        localStorage.removeItem(ASSESSMENT_SECRET_KEY);
      } finally {
        setLoading(false);
      }
    }

    checkExistingAssessment();
  }, []);

  // Start a new assessment via edge function
  const startAssessment = useCallback(async (info: {
    name: string;
    email: string;
    role?: string;
    practice_name?: string;
    token_id?: string;
  }) => {
    try {
      setSaving(true);
      setError(null);

      const { data, error } = await supabase.functions.invoke('create-assessment', {
        body: {
          name: info.name,
          email: info.email,
          role: info.role || null,
          practice_name: info.practice_name || null,
          token_id: info.token_id || null,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to create assessment');

      const newAssessment = data.assessment;

      // Store assessment ID and secret
      localStorage.setItem(ASSESSMENT_STORAGE_KEY, newAssessment.id);
      localStorage.setItem(ASSESSMENT_SECRET_KEY, newAssessment.session_secret);
      // Store session-specific key for persistent report access
      localStorage.setItem(`assessment_secret_${newAssessment.id}`, newAssessment.session_secret);

      setAssessment({
        id: newAssessment.id,
        name: info.name,
        email: info.email,
        role: info.role || null,
        practice_name: info.practice_name || null,
        created_at: new Date().toISOString(),
        completed_at: null,
        question_order: newAssessment.question_order,
        status: 'in_progress',
        current_question_index: 0,
        token_id: info.token_id || null,
      });
      setSessionSecret(newAssessment.session_secret);
      setCurrentIndex(0);
      setResponses(new Map());

      return { id: newAssessment.id, sessionSecret: newAssessment.session_secret };
    } catch (err) {
      console.error('Error starting assessment:', err);
      setError('Failed to start assessment');
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  // Save a response via edge function
  const saveResponse = useCallback(async (questionId: string, value: number) => {
    if (!assessment || !sessionSecret) return;

    try {
      setSaving(true);

      const newIndex = currentIndex + 1;

      const { data, error } = await supabase.functions.invoke('save-assessment-response', {
        body: {
          assessmentId: assessment.id,
          sessionSecret: sessionSecret,
          questionId: questionId,
          responseValue: value,
          currentQuestionIndex: newIndex,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to save response');

      // Update local state
      setResponses(prev => new Map(prev).set(questionId, value));
      setCurrentIndex(newIndex);
    } catch (err) {
      console.error('Error saving response:', err);
      setError('Failed to save response');
    } finally {
      setSaving(false);
    }
  }, [assessment, sessionSecret, currentIndex]);

  // Go to previous question
  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  // Complete the assessment
  const completeAssessment = useCallback(async () => {
    if (!assessment || !sessionSecret) return null;

    try {
      setSaving(true);

      const { data, error } = await supabase.functions.invoke('complete-assessment', {
        body: {
          assessmentId: assessment.id,
          sessionSecret: sessionSecret,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to complete assessment');

      // Clear localStorage after completion
      localStorage.removeItem(ASSESSMENT_STORAGE_KEY);
      localStorage.removeItem(ASSESSMENT_SECRET_KEY);

      return assessment.id;
    } catch (err) {
      console.error('Error completing assessment:', err);
      setError('Failed to complete assessment');
      return null;
    } finally {
      setSaving(false);
    }
  }, [assessment, sessionSecret]);

  // Clear assessment (resume later or abandon)
  const clearAssessment = useCallback(() => {
    localStorage.removeItem(ASSESSMENT_STORAGE_KEY);
    localStorage.removeItem(ASSESSMENT_SECRET_KEY);
    setAssessment(null);
    setSessionSecret(null);
    setResponses(new Map());
    setCurrentIndex(0);
  }, []);

  // Calculate estimated time remaining
  const estimatedTimeRemaining = useCallback(() => {
    const remaining = TOTAL_QUESTIONS - currentIndex;
    const secondsPerQuestion = 12; // ~12 seconds average per question
    const minutes = Math.ceil((remaining * secondsPerQuestion) / 60);
    return minutes;
  }, [currentIndex]);

  // Get current question info from the shuffled order
  const questionOrder = assessment?.question_order || [];
  const currentQuestionId = questionOrder[currentIndex] || null;
  const currentQuestionText = currentQuestionId 
    ? QUESTIONS_MAP.get(currentQuestionId)?.text || null 
    : null;

  // Progress percentage
  const progressPercentage = TOTAL_QUESTIONS > 0
    ? Math.round((currentIndex / TOTAL_QUESTIONS) * 100)
    : 0;

  return {
    assessment,
    sessionSecret,
    responses,
    currentIndex,
    loading,
    saving,
    error,
    progressPercentage,
    questionOrder,
    currentQuestionId,
    currentQuestionText,
    isComplete: currentIndex >= TOTAL_QUESTIONS && assessment !== null,
    hasExistingAssessment: assessment !== null,
    totalQuestions: TOTAL_QUESTIONS,
    startAssessment,
    saveResponse,
    goToPrevious,
    completeAssessment,
    clearAssessment,
    estimatedTimeRemaining,
  };
}
